import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import authService from '../services/authService';
import Navbar from './Navbar';
import Footer from './Footer';

const NewDiscussion = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isGeneral, setIsGeneral] = useState(false);
  const [selectedBoardGames, setSelectedBoardGames] = useState([]);
  const [selectedClubs, setSelectedClubs] = useState([]);
  const [boardGames, setBoardGames] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchBoardGamesAndClubs();
  }, [navigate]);

  const fetchBoardGamesAndClubs = async () => {
    try {
      const [gamesRes, clubsRes] = await Promise.all([
        fetch('/api/board-games?limit=100'),
        fetch('/api/clubs')
      ]);

      const gamesData = await gamesRes.json();
      const clubsData = await clubsRes.json();

      if (gamesData.success) setBoardGames(gamesData.data);
      if (clubsData.success) setClubs(clubsData.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!title.trim() || !content.trim()) {
      setFormError('Please fill in all required fields.');
      return;
    }

    try {
      setSubmitting(true);
      const response = await authService.post('/api/discussions', {
        title,
        content,
        isGeneral,
        boardGameIds: selectedBoardGames,
        clubIds: selectedClubs
      });

      if (response.success) {
        navigate(`/discussions/${response.data.slug}`);
      } else {
        setFormError(response.message || 'Error creating discussion.');
      }
    } catch (err) {
      console.error('Error creating discussion:', err);
      setFormError('Failed to create discussion.');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleBoardGame = (id) => {
    setSelectedBoardGames(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const toggleClub = (id) => {
    setSelectedClubs(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-base-200">
      <Navbar />
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Breadcrumb */}
        <div className="text-sm breadcrumbs mb-4">
          <ul>
            <li><Link to="/">Home</Link></li>
            <li><Link to="/discussions">Discussions</Link></li>
            <li>New Discussion</li>
          </ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card bg-base-100 shadow-xl"
        >
          <div className="card-body">
            <h1 className="card-title text-3xl md:text-4xl">Start a Discussion</h1>

            {formError && (
              <div className="alert alert-error mt-4">
                <span>{formError}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-6">
              {/* Title */}
              <div className="form-control mb-6">
                <label className="label">
                  <span className="label-text text-lg font-semibold">Title *</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="input input-bordered input-lg"
                  placeholder="What's on your mind?"
                  required
                />
              </div>

              {/* Content */}
              <div className="form-control mb-6">
                <label className="label">
                  <span className="label-text text-lg font-semibold">Content *</span>
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="textarea textarea-bordered h-48 text-base"
                  placeholder="Share your thoughts..."
                  required
                ></textarea>
              </div>

              {/* Discussion Type */}
              <div className="form-control mb-6">
                <label className="label cursor-pointer justify-start gap-4">
                  <input
                    type="checkbox"
                    checked={isGeneral}
                    onChange={(e) => setIsGeneral(e.target.checked)}
                    className="checkbox checkbox-primary"
                  />
                  <span className="label-text text-lg">
                    General Discussion (not specific to any game or club)
                  </span>
                </label>
              </div>

              {/* Link to Board Games */}
              {!isGeneral && (
                <div className="mb-6">
                  <label className="label">
                    <span className="label-text text-lg font-semibold">Link to Board Games (optional)</span>
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-base-300 rounded-lg p-4">
                    {boardGames.length > 0 ? (
                      <div className="space-y-2">
                        {boardGames.map(game => (
                          <label key={game.id} className="label cursor-pointer justify-start gap-4">
                            <input
                              type="checkbox"
                              checked={selectedBoardGames.includes(game.id)}
                              onChange={() => toggleBoardGame(game.id)}
                              className="checkbox checkbox-sm"
                            />
                            <span className="label-text">{game.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-base-content/70">Loading board games...</p>
                    )}
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-base-content/70">
                      Selected: {selectedBoardGames.length} board game(s)
                    </p>
                  </div>
                </div>
              )}

              {/* Link to Clubs */}
              {!isGeneral && (
                <div className="mb-6">
                  <label className="label">
                    <span className="label-text text-lg font-semibold">Link to Clubs (optional)</span>
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-base-300 rounded-lg p-4">
                    {clubs.length > 0 ? (
                      <div className="space-y-2">
                        {clubs.map(club => (
                          <label key={club.id} className="label cursor-pointer justify-start gap-4">
                            <input
                              type="checkbox"
                              checked={selectedClubs.includes(club.id)}
                              onChange={() => toggleClub(club.id)}
                              className="checkbox checkbox-sm"
                            />
                            <span className="label-text">{club.name}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-base-content/70">Loading clubs...</p>
                    )}
                  </div>
                  <div className="mt-2">
                    <p className="text-sm text-base-content/70">
                      Selected: {selectedClubs.length} club(s)
                    </p>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-4 mt-8">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn btn-primary btn-lg"
                >
                  {submitting ? 'Creating...' : 'Create Discussion'}
                </button>
                <Link to="/discussions" className="btn btn-ghost btn-lg">
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default NewDiscussion;
